import React, { useState, useEffect, useCallback, useRef } from "react"
import {
  AiOutlineCheck,
  AiOutlineClose,
  AiOutlineExclamationCircle,
  AiOutlineDollar,
  AiOutlineCreditCard,
  AiOutlineBuild,
  AiOutlineEdit,
} from "react-icons/ai"
import { Alert, Card, CardBody } from "reactstrap"

import toastHandler from "toastHandler"
import {
  startEvaluation,
  checkEvaluation,
  getEvaluationResult,
  getProcesoDeEvaluacion,
  guardarEvaluacion,
} from "api"

// ============================================
// Componente MontoInput - Entrada de montos con validación
// ============================================
const MontoInput = ({
  value,
  onChange,
  maxValue,
  disabled,
  placeholder = "0",
}) => {
  const [inputValue, setInputValue] = useState(value?.toString() || "")
  const [hasError, setHasError] = useState(false)

  useEffect(() => {
    setInputValue(value?.toString() || "")
    setHasError(false)
  }, [value])

  const handleInputChange = e => {
    const rawValue = e.target.value
    if (rawValue === "" || /^\d+$/.test(rawValue)) {
      setInputValue(rawValue)
      setHasError(false)
    }
  }

  const validateAndUpdate = () => {
    if (inputValue === "" || inputValue === "0") {
      setHasError(true)
      setInputValue("1")
      onChange(1)
      return
    }

    const numericValue = parseInt(inputValue)

    if (isNaN(numericValue) || numericValue <= 0) {
      setHasError(true)
      setInputValue("1")
      onChange(1)
      return
    }

    if (maxValue && numericValue > maxValue) {
      setHasError(true)
      setInputValue(maxValue.toString())
      onChange(maxValue)
      return
    }

    setHasError(false)
    onChange(numericValue)
  }

  const handleBlur = () => {
    validateAndUpdate()
  }

  const handleKeyPress = e => {
    if (e.key === "Enter") {
      validateAndUpdate()
      e.target.blur()
    }
  }

  return (
    <div className="input-group input-group-sm" style={{ maxWidth: "180px" }}>
      <span className="input-group-text">$</span>
      <input
        type="text"
        className={`form-control ${hasError ? "is-invalid" : ""}`}
        value={inputValue}
        onChange={handleInputChange}
        onBlur={handleBlur}
        onKeyPress={handleKeyPress}
        placeholder={placeholder}
        disabled={disabled}
        style={{ fontSize: "0.875rem" }}
      />
    </div>
  )
}

// ============================================
// Componente CalificacionGrid - Grilla editable de cupo/límites/líneas
// ============================================
const CalificacionGrid = ({
  calificacion,
  onCalificacionChange,
  disabled,
  setError,
}) => {
  if (!calificacion || calificacion.length === 0) return null

  const cal = calificacion[0]
  const limites = cal.calificacionlimite || []

  // Inicializar montocupo si no existe
  if (!cal.montocupo) {
    cal.montocupo = cal.monto
  }

  const montoOriginalCupo = cal.montocupo

  // Establecer montos originales para límites y líneas
  const inicializarMontosOriginales = () => {
    let calificacionModificada = false

    limites.forEach(limite => {
      if (!limite.montooriginal) {
        limite.montooriginal = limite.montocalculado
        calificacionModificada = true
      }

      if (limite.calificacionlinea) {
        limite.calificacionlinea.forEach(linea => {
          if (!linea.montooriginal) {
            linea.montooriginal = linea.montocalculado
            calificacionModificada = true
          }
        })
      }
    })

    if (calificacionModificada) {
      const nuevaCalificacion = [...calificacion]
      onCalificacionChange(nuevaCalificacion)
    }
  }

  inicializarMontosOriginales()

  const getIconForTipo = (tipo, level) => {
    if (level === "cupo") {
      return <AiOutlineDollar size={16} className="text-primary" />
    } else if (level === "limite") {
      switch (tipo) {
        case "LIM_FINANCIACIONES":
          return <AiOutlineBuild size={16} className="text-success" />
        case "LIM_DESCUENTOS":
          return <AiOutlineEdit size={16} className="text-warning" />
        case "LIM_TARJETAS":
          return <AiOutlineCreditCard size={16} className="text-info" />
        default:
          return <AiOutlineDollar size={16} className="text-secondary" />
      }
    } else {
      switch (tipo) {
        case 61:
          return <AiOutlineBuild size={14} className="text-muted" />
        case 72:
          return <AiOutlineCreditCard size={14} className="text-muted" />
        case 100:
          return <AiOutlineEdit size={14} className="text-muted" />
        case 201:
          return <AiOutlineCreditCard size={14} className="text-muted" />
        default:
          return <AiOutlineDollar size={14} className="text-muted" />
      }
    }
  }

  const handleMontoChange = (
    nuevoMonto,
    type,
    limiteIndex = null,
    lineaIndex = null,
  ) => {
    try {
      const nuevaCalificacion = [...calificacion]

      if (type === "cupo") {
        if (nuevoMonto > montoOriginalCupo) {
          setError(
            `El monto del cupo no puede exceder ${montoOriginalCupo.toLocaleString()} (máximo del evaluador)`,
          )
          return
        }

        nuevaCalificacion[0].monto = nuevoMonto

        if (nuevaCalificacion[0].calificacionlimite) {
          nuevaCalificacion[0].calificacionlimite.forEach(limite => {
            const montoOriginalLimite =
              limite.montooriginal || limite.montocalculado
            const nuevoMaximoLimite = Math.min(montoOriginalLimite, nuevoMonto)

            if (limite.montocalculado > nuevoMaximoLimite) {
              limite.montocalculado = nuevoMaximoLimite

              if (limite.calificacionlinea) {
                limite.calificacionlinea.forEach(linea => {
                  const montoOriginalLinea =
                    linea.montooriginal || linea.montocalculado
                  const nuevoMaximoLinea = Math.min(
                    montoOriginalLinea,
                    nuevoMaximoLimite,
                  )

                  if (linea.montocalculado > nuevoMaximoLinea) {
                    linea.montocalculado = nuevoMaximoLinea
                  }
                })
              }
            }
          })
        }
      } else if (type === "limite") {
        const limite = nuevaCalificacion[0].calificacionlimite[limiteIndex]
        const cupoActual = nuevaCalificacion[0].monto
        const montoOriginalLimite =
          limite.montooriginal || limite.montocalculado

        if (nuevoMonto > montoOriginalLimite) {
          setError(
            `El monto del límite no puede exceder ${montoOriginalLimite.toLocaleString()} (máximo del evaluador)`,
          )
          return
        }

        if (nuevoMonto > cupoActual) {
          setError(
            `El monto del límite no puede exceder ${cupoActual.toLocaleString()} (monto actual del cupo)`,
          )
          return
        }

        nuevaCalificacion[0].calificacionlimite[limiteIndex].montocalculado =
          nuevoMonto

        if (limite.calificacionlinea) {
          limite.calificacionlinea.forEach(linea => {
            const montoOriginalLinea =
              linea.montooriginal || linea.montocalculado
            const nuevoMaximoLinea = Math.min(montoOriginalLinea, nuevoMonto)

            if (linea.montocalculado > nuevoMaximoLinea) {
              linea.montocalculado = nuevoMaximoLinea
            }
          })
        }
      } else if (type === "linea") {
        const limite = nuevaCalificacion[0].calificacionlimite[limiteIndex]
        const linea = limite.calificacionlinea[lineaIndex]
        const limiteActual = limite.montocalculado
        const montoOriginalLinea = linea.montooriginal || linea.montocalculado

        if (nuevoMonto > montoOriginalLinea) {
          setError(
            `El monto de la línea no puede exceder ${montoOriginalLinea.toLocaleString()} (máximo del evaluador)`,
          )
          return
        }

        if (nuevoMonto > limiteActual) {
          setError(
            `La línea no puede superar su límite: ${limiteActual.toLocaleString()} (monto actual del límite)`,
          )
          return
        }

        nuevaCalificacion[0].calificacionlimite[limiteIndex].calificacionlinea[
          lineaIndex
        ].montocalculado = nuevoMonto
      }

      onCalificacionChange(nuevaCalificacion)
    } catch (error) {
      setError("Error al actualizar montos: " + error.message)
    }
  }

  const handleDeleteLinea = (limiteIndex, lineaIndex) => {
    if (window.confirm("¿Está seguro de eliminar esta línea?")) {
      try {
        const nuevaCalificacion = [...calificacion]
        nuevaCalificacion[0].calificacionlimite[
          limiteIndex
        ].calificacionlinea.splice(lineaIndex, 1)

        if (
          nuevaCalificacion[0].calificacionlimite[limiteIndex].calificacionlinea
            .length === 0
        ) {
          nuevaCalificacion[0].calificacionlimite.splice(limiteIndex, 1)
        }

        onCalificacionChange(nuevaCalificacion)
      } catch (error) {
        setError("Error al eliminar línea: " + error.message)
      }
    }
  }

  // Crear array de filas para la grilla
  const rows = []

  // Fila del cupo
  rows.push({
    id: "cupo",
    level: "cupo",
    icon: getIconForTipo(null, "cupo"),
    name: cal.limite || "Cupo Principal",
    tipo: "Cupo",
    monto: cal.monto,
    montoOriginal: montoOriginalCupo,
    moneda: "ARS",
    vencimiento: cal.fechavencimiento
      ? new Date(cal.fechavencimiento).toLocaleDateString()
      : "N/A",
    estado: cal.estado,
    maxMonto: montoOriginalCupo,
    maxDisplay: cal.montocupo,
    onMontoChange: newMonto => handleMontoChange(newMonto, "cupo"),
    showActions: false,
  })

  // Filas de límites y líneas
  limites.forEach((limite, limiteIndex) => {
    rows.push({
      id: `limite-${limiteIndex}`,
      level: "limite",
      icon: getIconForTipo(limite.codigo, "limite"),
      name: limite.nombre,
      tipo: `Límite BT: ${limite.tipobantotal}`,
      monto: limite.montocalculado,
      moneda: "ARS",
      vencimiento: cal.fechavencimiento
        ? new Date(cal.fechavencimiento).toLocaleDateString()
        : "N/A",
      estado: limite.estado,
      maxMonto: Math.min(
        limite.montooriginal || limite.montocalculado,
        cal.monto,
      ),
      onMontoChange: newMonto =>
        handleMontoChange(newMonto, "limite", limiteIndex),
      showActions: false,
    })

    if (limite.calificacionlinea) {
      limite.calificacionlinea.forEach((linea, lineaIndex) => {
        rows.push({
          id: `linea-${limiteIndex}-${lineaIndex}`,
          level: "linea",
          icon: getIconForTipo(linea.tipobantotal, "linea"),
          name: linea.nombre,
          tipo: `Línea BT: ${linea.tipobantotal}`,
          monto: linea.montocalculado,
          moneda: "ARS",
          vencimiento: linea.fechavencimiento
            ? new Date(linea.fechavencimiento).toLocaleDateString()
            : "Igual al cupo",
          estado: linea.estado,
          maxMonto: Math.min(
            linea.montooriginal || linea.montocalculado,
            limite.montocalculado,
          ),
          onMontoChange: newMonto =>
            handleMontoChange(newMonto, "linea", limiteIndex, lineaIndex),
          showActions: true,
          onDelete: () => handleDeleteLinea(limiteIndex, lineaIndex),
          limiteIndex,
          lineaIndex,
        })
      })
    }
  })

  return (
    <div className="calificacion-grid">
      <div className="table-responsive">
        <table className="table table-hover">
          <thead className="table-light">
            <tr>
              <th style={{ width: "35%" }}>Producto</th>
              <th style={{ width: "15%" }}>Tipo</th>
              <th style={{ width: "20%" }}>Monto</th>
              <th style={{ width: "10%" }}>Moneda</th>
              <th style={{ width: "15%" }}>Vencimiento</th>
              <th style={{ width: "5%" }}>Estado</th>
              {!disabled && <th style={{ width: "5%" }}>Acciones</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map(row => (
              <tr key={row.id} className={`nivel-${row.level}`}>
                <td>
                  <div
                    className="d-flex align-items-center"
                    style={{
                      paddingLeft:
                        row.level === "cupo"
                          ? "0px"
                          : row.level === "limite"
                            ? "20px"
                            : "40px",
                    }}
                  >
                    {row.icon}
                    <span
                      className={`ms-2 ${row.level === "cupo" ? "fw-bold" : row.level === "limite" ? "fw-semibold" : ""}`}
                    >
                      {row.name}
                    </span>
                  </div>
                </td>
                <td>
                  <small className="text-muted">{row.tipo}</small>
                </td>
                <td>
                  <div
                    className="input-group input-group-sm"
                    style={{ maxWidth: "180px" }}
                  >
                    <MontoInput
                      value={row.monto}
                      onChange={row.onMontoChange}
                      maxValue={row.maxMonto}
                      disabled={disabled}
                    />
                  </div>
                  <small className="text-muted d-block">
                    Máx: ${(row.maxDisplay || row.maxMonto)?.toLocaleString()}
                  </small>
                </td>
                <td>
                  <span className="badge bg-primary">{row.moneda}</span>
                </td>
                <td>
                  <small>{row.vencimiento}</small>
                </td>
                <td>
                  <span
                    className={`badge ${row.estado === "Activo" ? "bg-success" : "bg-secondary"}`}
                  >
                    {row.estado}
                  </span>
                </td>
                {!disabled && (
                  <td>
                    {row.showActions && (
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={row.onDelete}
                        title="Eliminar línea"
                        disabled={disabled}
                      >
                        <AiOutlineClose size={14} />
                      </button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Información adicional */}
      <div className="row mt-3">
        <div className="col-md-6">
          <div className="card bg-light">
            <div className="card-body py-2">
              <small className="text-muted">
                <strong>Monto Original del Evaluador:</strong> $
                {montoOriginalCupo?.toLocaleString()}
              </small>
            </div>
          </div>
        </div>
        <div className="col-md-6">
          <div className="card bg-light">
            <div className="card-body py-2">
              <small className="text-muted">
                <strong>Plazo:</strong> {cal.plazo} meses |
                <strong className="ms-2">Garantía:</strong> {cal.tipogarantia}
              </small>
            </div>
          </div>
        </div>
      </div>

      {/* Advertencias */}
      <div className="row mt-2">
        <div className="col-12">
          <div className="alert alert-info py-2">
            <small>
              <strong>Nota:</strong> Al eliminar una línea, si el límite queda
              sin líneas se eliminará automáticamente. Los montos deben ser
              mayores a cero y no pueden exceder los límites superiores.
            </small>
          </div>
        </div>
      </div>
    </div>
  )
}

// ============================================
// Componente principal Evaluacion para Onboarding
// ============================================
const Evaluacion = ({
  props,
  cuit,
  setCalificacionElegida,
  setEvaluacionCompleta,
}) => {
  const { setError } = toastHandler()
  const setSuccess = toastHandler(state => state.setSuccess)

  const [loading, setLoading] = useState(false)
  const [progressMessage, setProgressMessage] = useState("")
  const [evaluacion, setEvaluacion] = useState(null)
  const [cliente, setCliente] = useState(null)
  const [calificacionEditable, setCalificacionEditable] = useState([])
  const [errorState, setErrorState] = useState(null)

  // Ref para guardar el CUIT que ya fue evaluado y evitar doble ejecución
  const cuitEvaluado = useRef(null)

  const { formik } = props
  const { values } = formik

  // Limpiar error
  const clearError = () => {
    setErrorState(null)
  }

  // Manejar error
  const handleError = errorMessage => {
    setError(errorMessage)
    setErrorState(errorMessage)
  }

  // Función para manejar cambios en la calificación editable
  const handleCalificacionChange = nuevaCalificacion => {
    setCalificacionEditable(nuevaCalificacion)
    // Actualizar la calificación elegida para el componente padre
    if (nuevaCalificacion && nuevaCalificacion.length > 0) {
      setCalificacionElegida(nuevaCalificacion[0])

      // También actualizar la evaluación completa con la calificación editada
      if (setEvaluacionCompleta && evaluacion) {
        setEvaluacionCompleta({
          ...evaluacion,
          calificacion: nuevaCalificacion,
        })
      }
    }
  }

  // Función principal de evaluación
  const realizarEvaluacion = async () => {
    if (!cuit) {
      handleError("No se encontró el CUIT del titular")
      return
    }

    // Marcar el CUIT como evaluado para evitar re-ejecuciones
    cuitEvaluado.current = cuit

    setEvaluacion(null)
    setCliente(null)
    setCalificacionEditable([])
    setLoading(true)
    clearError()
    setProgressMessage("Iniciando evaluación...")

    try {
      // Obtener procesos de evaluación para usar el primero disponible
      const procesosData = await getProcesoDeEvaluacion()

      if (!procesosData || procesosData.length === 0) {
        throw new Error("No hay procesos de evaluación disponibles")
      }

      // Usar el primer proceso disponible (o el código 4 si existe)
      const procesoSeleccionado =
        procesosData.find(p => p.id === 4) || procesosData[0]
      const selectedProceso = procesoSeleccionado.id
      const selectedTipoProceso = procesoSeleccionado.codigo

      setProgressMessage("Conectando con el evaluador...")

      // Iniciar evaluación
      const evaluacionId = await startEvaluation(
        selectedProceso,
        selectedTipoProceso,
        cuit,
      )

      if (!evaluacionId) {
        throw new Error("No se recibió ID de evaluación")
      }

      // Polling del estado
      const checkStatus = async () => {
        try {
          setProgressMessage("Consultando estado...")
          const evaluation = await checkEvaluation(evaluacionId)

          if (
            evaluation.estado !== "Completado" &&
            evaluation.estado !== "Error"
          ) {
            setTimeout(checkStatus, 7000)
          } else {
            setProgressMessage("Obteniendo resultados...")

            // Obtener resultado
            const evaluacionData = await getEvaluationResult(evaluacionId)

            // Guardar evaluación en backend
            try {
              setProgressMessage("Guardando evaluación...")
              await guardarEvaluacion(evaluacionData)
            } catch (saveError) {
              console.error(
                "Error al guardar evaluación en backend:",
                saveError,
              )
            }

            setEvaluacion(evaluacionData)
            // Guardar la evaluación completa para el componente padre
            if (setEvaluacionCompleta) {
              setEvaluacionCompleta(evaluacionData)
            }
            setCliente({
              tipodocumento: evaluacionData.persona?.tipodocumento || "CUIT",
              numerodocumento: cuit,
              nombre: evaluacionData.persona?.nombre || "",
              direccion: evaluacionData.persona?.direccion || "",
              actividad: evaluacionData.persona?.actividad || "",
              score: evaluacionData.persona?.score || 0,
            })

            // Si tiene calificación, inicializar la versión editable
            if (
              evaluacionData.calificacion &&
              evaluacionData.calificacion.length > 0
            ) {
              setSuccess("Evaluación realizada exitosamente")
              setCalificacionEditable([...evaluacionData.calificacion])
              setCalificacionElegida(evaluacionData.calificacion[0])
            } else if (
              evaluacionData.errorMessage ||
              evaluacionData.resultadofinal !== "Aceptado"
            ) {
              handleError(
                "Hubo problemas en la evaluación: " +
                  (evaluacionData.errorMessage || "Cliente no aprobado"),
              )
            }

            setLoading(false)
          }
        } catch (error) {
          handleError(error.message)
          setEvaluacion(null)
          setCliente(null)
          setCalificacionEditable([])
          setLoading(false)
        }
      }

      checkStatus()
    } catch (error) {
      console.error("Error al realizar la evaluación:", error)
      const errorMessage =
        error.message ||
        "Ocurrió un error al realizar la evaluación. Por favor, intente nuevamente."
      handleError(errorMessage)
      setEvaluacion(null)
      setCliente(null)
      setCalificacionEditable([])
      setLoading(false)
    }
  }

  // Efecto para disparar la evaluación automáticamente (solo una vez por CUIT)
  useEffect(() => {
    // Solo ejecutar si hay CUIT y es diferente al que ya se evaluó
    if (cuit && cuit !== cuitEvaluado.current) {
      realizarEvaluacion()
    }
  }, [cuit])

  // Efecto para el cursor de carga
  useEffect(() => {
    document.body.style.cursor = loading ? "wait" : "default"
    return () => {
      document.body.style.cursor = "default"
    }
  }, [loading])

  // Renderizado de loading
  if (loading) {
    return (
      <div className="text-center mb-5">
        <h4>
          {progressMessage || "Obteniendo oferta..."}
          <br />
          <i className="bx bx-buoy bx-spin text-primary display-3" />
        </h4>
      </div>
    )
  }

  // Renderizado principal
  return (
    <Card>
      <CardBody>
        {/* Sección de error */}
        {(errorState || evaluacion?.errorMessage) && (
          <div className="error-section mb-4">
            <Alert
              color="danger"
              className="d-flex align-items-center"
              toggle={clearError}
            >
              <AiOutlineExclamationCircle size={24} className="me-3" />
              <div className="flex-grow-1">
                <h5 className="mb-1 fw-bold">Error en la evaluación</h5>
                <p className="mb-0">{errorState || evaluacion?.errorMessage}</p>
              </div>
            </Alert>
          </div>
        )}

        {/* Datos del cliente */}
        {cliente && evaluacion && (
          <>
            <div className="client-section card shadow-sm mb-4">
              <div className="card-header bg-light">
                <h5 className="mb-0">Datos del cliente</h5>
              </div>
              <div className="card-body">
                <div className="row g-3">
                  <div className="col-md-6">
                    <div className="client-data-item">
                      <span className="label fw-bold">
                        Tipo y N° Documento:{" "}
                      </span>
                      <span className="value">
                        {cliente.tipodocumento} {cliente.numerodocumento}
                      </span>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="client-data-item">
                      <span className="label fw-bold">Razón Social: </span>
                      <span className="value">{cliente.nombre}</span>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="client-data-item">
                      <span className="label fw-bold">Dirección: </span>
                      <span className="value">{cliente.direccion}</span>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="client-data-item">
                      <span className="label fw-bold">Actividad: </span>
                      <span className="value">{cliente.actividad}</span>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="client-data-item">
                      <span className="label fw-bold">Score: </span>
                      <span className="value">{cliente.score}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Resultado de evaluación */}
            <div className="result-section card shadow-sm mb-4">
              <div className="card-header bg-light">
                <h5 className="mb-0">Resultado de evaluación</h5>
              </div>
              <div className="card-body">
                <Alert
                  color={
                    evaluacion.resultadofinal === "Aceptado"
                      ? "success"
                      : "danger"
                  }
                  className="mb-4 d-flex justify-content-between align-items-center"
                >
                  <div>
                    {evaluacion.resultadofinal === "Aceptado" ? (
                      <AiOutlineCheck size={24} className="me-3" />
                    ) : (
                      <AiOutlineClose size={24} className="me-3" />
                    )}
                    <strong className="mb-0 fw-bold fs-5">
                      {evaluacion.resultadofinal ?? evaluacion.errorMessage}
                    </strong>
                  </div>
                  <div>ID: #{evaluacion.id}</div>
                </Alert>

                {/* Información de Scoring */}
                {evaluacion.scoring && (
                  <div className="mt-3">
                    <div className="card bg-light">
                      <div className="card-body">
                        <h6 className="card-title">Información de Scoring</h6>
                        <div className="row">
                          <div className="col-md-3">
                            <div className="mb-2">
                              <span className="text-muted">Score Total:</span>
                              <div className="fw-bold">
                                {evaluacion.scoring.score}
                              </div>
                            </div>
                          </div>
                          <div className="col-md-3">
                            <div className="mb-2">
                              <span className="text-muted">
                                Score Financiero:
                              </span>
                              <div className="fw-bold">
                                {evaluacion.scoring.scoreFinanciero}
                              </div>
                            </div>
                          </div>
                          <div className="col-md-3">
                            <div className="mb-2">
                              <span className="text-muted">
                                Score Crediticio:
                              </span>
                              <div className="fw-bold">
                                {evaluacion.scoring.scoreCrediticio}
                              </div>
                            </div>
                          </div>
                          <div className="col-md-3">
                            <div className="mb-2">
                              <span className="text-muted">
                                Nivel de Riesgo:
                              </span>
                              <div className="fw-bold">
                                {evaluacion.scoring.nivelRiesgo}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Calificación (Solo para aprobados) */}
            {evaluacion.resultadofinal === "Aceptado" &&
            evaluacion.calificacion &&
            evaluacion.calificacion.length > 0 ? (
              <div className="qualification-section card shadow-sm mb-4">
                <div className="card-header bg-light">
                  <h5 className="mb-0">Calificación del cliente</h5>
                  <small className="text-muted">
                    Puede ajustar los valores pero no exceder los montos
                    propuestos
                  </small>
                </div>
                <div className="card-body">
                  <CalificacionGrid
                    calificacion={calificacionEditable}
                    onCalificacionChange={handleCalificacionChange}
                    disabled={loading}
                    setError={handleError}
                  />
                </div>
              </div>
            ) : evaluacion.resultadofinal !== "Aceptado" ? (
              <div className="no-offer-section card shadow-sm mb-4">
                <div className="card-body">
                  <Alert color="warning" className="d-flex align-items-center">
                    <AiOutlineExclamationCircle size={24} className="me-3" />
                    <div>
                      <h5 className="mb-0 fw-bold">
                        En este momento no hay oferta disponible para el
                        cliente.
                      </h5>
                    </div>
                  </Alert>
                </div>
              </div>
            ) : (
              <div className="no-qualification-section card shadow-sm mb-4">
                <div className="card-body">
                  <Alert color="info" className="d-flex align-items-center">
                    <AiOutlineExclamationCircle size={24} className="me-3" />
                    <div>
                      <h5 className="mb-0 fw-bold">
                        No hay calificaciones disponibles para este cliente.
                      </h5>
                    </div>
                  </Alert>
                </div>
              </div>
            )}

            {/* CDAs Rechazados */}
            {evaluacion.resultadoscriterios &&
              evaluacion.resultadoscriterios.filter(
                criterio =>
                  criterio.resultado === "Rechazado" ||
                  criterio.resultado === "Error",
              ).length > 0 && (
                <div className="rejected-cdas-section card shadow-sm mb-4">
                  <div className="card-header bg-light">
                    <h5 className="mb-0">
                      {`Criterios de aceptación rechazados ${evaluacion.resultadoscriterios.filter(criterio => criterio.resultado === "Error").length > 0 ? "o con error" : ""}`}
                    </h5>
                  </div>
                  <div className="card-body p-0">
                    <div className="table-responsive">
                      <table className="table mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>Código</th>
                            <th>Título</th>
                            <th>Descripción</th>
                            <th>Valor obtenido</th>
                            <th>Resultado</th>
                          </tr>
                        </thead>
                        <tbody>
                          {evaluacion.resultadoscriterios
                            .filter(
                              criterio =>
                                criterio.resultado === "Rechazado" ||
                                criterio.resultado === "Error",
                            )
                            .map((criterio, index) => (
                              <tr key={index}>
                                <td>
                                  <span className="badge bg-secondary">
                                    {criterio.codigo}
                                  </span>
                                </td>
                                <td>{criterio.titulo}</td>
                                <td>{criterio.descripcion}</td>
                                <td>{criterio.valorobtenido}</td>
                                <td>
                                  <span
                                    className={
                                      criterio.resultado === "Rechazado" ||
                                      criterio.resultado === "Error"
                                        ? "badge bg-danger"
                                        : criterio.resultado === "Derivar"
                                          ? "badge bg-warning"
                                          : "badge bg-success"
                                    }
                                  >
                                    {criterio.resultado}
                                  </span>
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
          </>
        )}

        {/* Mensaje cuando no hay evaluación y no está cargando */}
        {!loading && !evaluacion && !errorState && (
          <div className="text-center py-5">
            <AiOutlineExclamationCircle size={48} className="text-muted mb-3" />
            <h5 className="text-muted">
              No se ha podido iniciar la evaluación
            </h5>
            <p className="text-muted">
              Verifique que los datos del titular estén completos
            </p>
            <button
              className="btn btn-primary mt-3"
              onClick={() => {
                cuitEvaluado.current = null
                realizarEvaluacion()
              }}
            >
              Reintentar evaluación
            </button>
          </div>
        )}
      </CardBody>
    </Card>
  )
}

export default Evaluacion
